import { PermissionDTO } from "../../auth/context/useAuthContext";

export type PermissionAccountType = "Admin" | "Collector";

export interface PermissionPresentation {
  title: string;
  description?: string;
  prerequisite?: string;
  scopeNote?: string;
  statusNotice?: string;
  needsReview: boolean;
}

type ConfirmedPermissionPresentation = Omit<PermissionPresentation, "needsReview">;

// Match verified Admin permission keys only. Keep API values unchanged.
const adminPermissionPresentations: Record<
  string,
  ConfirmedPermissionPresentation
> = {
  DashboardMenuVisible: {
    title: "매출 대시보드 메뉴 열기",
    description: "사이드 메뉴에 매출 대시보드로 가는 메뉴를 표시합니다.",
  },
  UserDetailRideHistoryVisible: {
    title: "유저·기기 상세의 여정내역 표시",
    description: "유저 상세와 기기 상세에 해당 유저·기기의 여정내역 표를 표시합니다.",
    prerequisite: "유저 상세에서는 유저 메뉴 권한이 필요합니다. 기기 상세는 계정별 접근 조건도 충족해야 합니다.",
  },
  ExtraPayButtonVisible: {
    title: "추가 결제·패널티 부과",
    description: "유저 상세의 추가 결제 요청·내역과 여정의 추가 결제·패널티 부과 기능을 표시합니다. 실제 청구는 결제 확정 단계에서 이루어집니다.",
    prerequisite: "유저 상세에서는 유저 메뉴 권한과 등록 카드가 필요합니다. 여정의 추가 결제·패널티는 본사 밴드 계정만 표시합니다.",
  },
  AreaMenuVisible: {
    title: "지오펜스 구역 메뉴 열기",
    description: "지오펜스와 임의대상 지오펜스 리스트 메뉴를 표시합니다. 같은 메뉴 아래에서 수거대상 리스트·맵과 콜렉터 구역맵을 찾을 수 있습니다.",
  },
  RepayAllow: {
    title: "실패한 여정 결제 다시 청구하기",
    description: "라이드·여정에서 실패한 결제를 다시 청구하는 재결제 버튼을 표시합니다.",
    prerequisite: "여정 환불·결제처리 권한과 라이드 또는 여정내역 접근 권한이 필요합니다. 본사 밴드 계정이며 실패 결제·잔액 있음·SWAP 결제 아님 조건을 충족해야 합니다.",
  },
  ZobaMenuEnable: {
    title: "운영·시냅스 메뉴 열기",
    description: "사이드 메뉴에 운영 메뉴를 표시합니다. 캠프관리도 이 메뉴 아래에 있습니다.",
    prerequisite: "수거구역·배치구역 메뉴는 본사 밴드 계정만 표시합니다. 시냅스 추천작업은 추가 권한이 필요합니다.",
  },
  TaskQuantityEditEnable: {
    title: "작업 수량 수정 화면 열기",
    description: "작업관리의 작업 수량 표에 수정 버튼을 표시하여 수정 모드로 들어갈 수 있게 합니다.",
    prerequisite: "작업관리 메뉴 권한이 필요합니다. 실제 입력에는 직원·지입 중 대상 권한과 완료·배정 중 수량 권한을 추가로 부여해야 합니다.",
  },
  OsTaskQuantityEditEnable: {
    title: "지입기사 작업 수량 입력",
    description: "작업관리에서 지입기사 행의 작업 수량을 입력할 수 있게 합니다.",
    prerequisite: "작업관리 메뉴·작업 수량 수정 진입 권한이 필요합니다. 입력할 항목에 따라 완료 수량 또는 배정 수량 입력 권한도 필요합니다.",
  },
  UpdateBelongingEnable: {
    title: "캠프 기사 소속 생성",
    description: "캠프관리의 기사 정보에서 새 소속을 만드는 소속 생성 버튼을 표시합니다.",
    prerequisite: "운영·시냅스 메뉴 권한이 필요합니다.",
  },
  DataDashboardVisible: {
    title: "신규 매출 대시보드 메뉴 열기",
    description: "대시보드 아래에 신규대시보드 메뉴를 표시합니다.",
    prerequisite: "상위 메뉴인 매출 대시보드 메뉴 권한도 필요합니다.",
  },
  TaskRevenueExportEnable: {
    title: "작업 정산 엑셀 다운로드 창 열기",
    description: "작업관리 상단에 정산 엑셀 버튼을 표시하고 정산 실적 다운로드 창을 열 수 있게 합니다.",
    prerequisite: "작업관리 메뉴 권한이 필요합니다. 실제 파일을 내려받으려면 내역·정산 파일 다운로드 권한도 필요합니다.",
    scopeNote: "다운로드 창을 여는 권한과 실제 파일 다운로드 권한이 나뉘어 있습니다.",
  },
  AccountMenuVisible: {
    title: "콜렉터 권한 설정·계정 삭제",
    description: "작업관리의 콜렉터 상세에서 권한 설정과 계정 삭제 버튼을 활성화합니다.",
    prerequisite: "작업관리 메뉴 권한이 필요합니다. 목록에서 상세로 이동하려면 콜렉터 상세 링크 표시 권한도 필요합니다.",
  },
  HistoryMenuVisible: {
    title: "기기 전체기록·QR 변경이력 메뉴 열기",
    description: "기기 메뉴 아래에 전체기록과 QR코드 변경이력 메뉴를 표시합니다.",
    prerequisite: "QR 변경이력에는 기기 메뉴 권한이 필요합니다. 계정 유형에 따라 전체기록 메뉴만 표시될 수 있습니다.",
  },
  CouponButtonVisible: {
    title: "유저 쿠폰 조회·발급·삭제",
    description: "유저 상세에 쿠폰 목록을 표시합니다. 쿠폰 발급·파트너 쿠폰 발급·삭제 기능도 함께 표시합니다.",
    prerequisite: "유저 메뉴 권한이 필요합니다.",
  },
  RefundButtonVisible: {
    title: "여정 결제 환불·미결제 처리",
    description: "라이드·여정에 환불 버튼을 표시합니다. 실패한 결제에는 입금처리·삭제 버튼도 함께 표시합니다.",
    prerequisite: "라이드 또는 여정내역 접근 권한이 필요합니다. SWAP 제외·잔액 조건이 있으며, 실패 결제의 입금처리·삭제는 본사 밴드 계정만 표시합니다.",
    scopeNote: "유저 상세의 별도 결제내역 환불 버튼은 이 권한으로 제어하지 않습니다. 재결제 버튼은 재결제 권한도 필요합니다.",
  },
  ShowAllSales: {
    title: "매출 대시보드에서 전체지역 매출 조회",
    description: "매출 대시보드에서 전체지역 매출을 선택하고 조회할 수 있는 범위를 확장합니다.",
    prerequisite: "매출 대시보드 메뉴 권한이 필요합니다. 신규대시보드를 이용하려면 신규대시보드 메뉴 권한도 필요합니다. 밴드 선택은 본사 밴드 계정만 표시합니다.",
    scopeNote: "다른 모든 화면의 데이터 조회 범위를 확장하는 권한은 아닙니다.",
  },
  TaskManagementVisible: {
    title: "작업 수량·수당 관리 메뉴 열기",
    description: "사이드 메뉴에 작업관리를 표시합니다. 작업 수량·수당·정산 기능은 이 화면에서 찾을 수 있습니다.",
    prerequisite: "수량·수당 입력, 수정, 다운로드에는 각 기능의 추가 권한이 필요합니다. 운영주체·밴드 필터 변경은 본사 밴드 계정만 가능합니다.",
  },
  TaskCompensationEditEnable: {
    title: "콜렉터별 추가수당 입력·수정",
    description: "작업관리의 직원·지입 작업 수당 표에서 추가수당의 종류·단가·수량·메모를 수정하고 저장할 수 있게 합니다.",
    prerequisite: "작업관리 메뉴 권한이 필요합니다. 직원 월급을 입력하려면 직원 월급 입력 권한도 필요합니다.",
    scopeNote: "개인별 추가수당을 바꾸는 기능입니다. 추가 수당표의 기준단가를 바꾸는 추가수당 기준단가 수정 권한과 구분합니다.",
  },
  AllottedTaskQuantityEditEnable: {
    title: "작업 배정 수량 입력",
    description: "작업관리의 작업 수량 수정 모드에서 배정 수량 입력칸을 활성화합니다.",
    prerequisite: "작업관리 메뉴·작업 수량 수정 진입 권한과 대상에 맞는 직원 또는 지입기사 작업 수량 권한이 필요합니다.",
    scopeNote: "완료 수량을 입력하려면 작업 완료 수량 입력 권한을 별도로 부여해야 합니다.",
  },
  DynamicPricingManualSetup: {
    title: "기기 임시 할인율 수동 설정",
    description: "기기 목록과 상세에서 선택한 기기의 임시 할인율을 설정하는 DP 기능을 표시합니다.",
    prerequisite: "기기 메뉴 권한이 필요합니다. 한 번에 최대 30대를 설정할 수 있으며, 기기 상세에서는 상태 변경이 금지된 밴드 계정을 제한합니다.",
  },
  Download: {
    title: "내역·정산 파일 다운로드",
    description: "라이드·여정·기기 전체기록·미결제·정산 실적에서 다운로드 버튼 또는 파일 다운로드를 허용합니다.",
    prerequisite: "다운로드할 화면에 접근할 수 있어야 합니다. 정산 엑셀은 작업 정산 엑셀 다운로드 창 권한도 필요합니다.",
    scopeNote: "기기 목록의 추출 등 이 권한을 사용하지 않는 다운로드 기능도 있습니다. 어드민 전체 다운로드를 통제하는 권한은 아닙니다.",
  },
  AllocatePointMenuVisible: {
    title: "배치포인트 지도·목록 메뉴 열기",
    description: "배치포인트 맵과 기종별 배치포인트 리스트로 가는 메뉴를 표시합니다.",
  },
  ScootStorageMenuVisible: {
    title: "기기 입출고 메뉴 열기",
    description: "입출고 메뉴와 입출고 현황·내역 화면으로 가는 메뉴를 표시합니다.",
    scopeNote: "입출고 일자 열만 표시하는 권한이 아니라 입출고 메뉴의 표시 조건입니다.",
  },
  VehicleStockOut: {
    title: "기기 상세·목록에서 입출고 조회·출고 처리",
    description: "현재 재고와 입고 기록을 조회하고, 실물 확인·사유 입력 후 거리 제한 없이 선택한 재고를 출고합니다.",
    prerequisite: "기기 상세에 접근할 수 있어야 합니다. 탑승 중이거나 차량 자산을 확정할 수 없는 기기는 출고할 수 없습니다.",
    scopeNote: "출고는 배치 대기·대여 불가 상태로 변경하며 배치 요청은 보내지 않습니다. 미확정 입고 기록의 보정은 출고 화면에서 별도 동의가 필요합니다.",
  },
  makeGeofenceMenuVisible: {
    title: "지오펜스 메뉴 열기",
    description: "지오펜스 메뉴와 수거구역 리스트·맵 등의 메뉴를 표시합니다. 지오펜스 구역 메뉴 권한과 둘 중 하나가 있으면 표시됩니다.",
    scopeNote: "새 지오펜스 앱의 편집 권한은 별도 SSO의 geofence 도메인 admin 역할에서 관리합니다.",
  },
  EtcMenuVisible: {
    title: "밴드·창고·지입 실적 메뉴 열기",
    description: "기타 메뉴 아래에 밴드 리스트·창고 리스트·지입기사 실적관리 메뉴를 표시합니다.",
  },
  DepositControlEnable: {
    title: "유저 보증금 조회·환불",
    description: "유저 상세에 보증금 잔액·거래내역과 보증금 환불 기능을 표시합니다.",
    prerequisite: "유저 메뉴 권한이 필요합니다. 보증금 정보가 있어야 환불 버튼을 표시합니다.",
  },
  ExtraTaskPriceEditEnable: {
    title: "추가수당 기준단가 수정",
    description: "작업관리 상단의 추가 수당표에서 기본 단가 입력칸과 적용 버튼을 활성화합니다.",
    prerequisite: "작업관리 메뉴 권한이 필요합니다.",
    scopeNote: "추가 수당표의 기준단가를 바꾸는 기능입니다. 개인별 추가수당 입력은 콜렉터별 추가수당 입력·수정 권한으로 제어합니다.",
  },
  SalaryEditEnable: {
    title: "직원 월급 입력",
    description: "작업관리의 직원 작업 수당 수정 모드에서 월급 입력칸을 활성화합니다.",
    prerequisite: "작업관리 메뉴와 콜렉터별 추가수당 입력·수정 권한이 함께 필요합니다.",
  },
  TaskResultQuantityEditEnable: {
    title: "작업 완료 수량 입력",
    description: "작업관리의 작업 수량 수정 모드에서 완료 수량 입력칸을 활성화합니다.",
    prerequisite: "작업관리 메뉴·작업 수량 수정 진입 권한과 대상에 맞는 직원 또는 지입기사 작업 수량 권한이 필요합니다.",
    scopeNote: "배정 수량을 입력하려면 작업 배정 수량 입력 권한을 별도로 부여해야 합니다.",
  },
  ZobaJobEnable: {
    title: "시냅스 추천작업 메뉴 표시 조건",
    description: "운영 메뉴의 시냅스 추천작업 항목을 표시할 때 함께 확인하는 권한입니다.",
    prerequisite: "운영·시냅스 메뉴 권한과 시냅스 추천작업 화면 권한이 모두 필요합니다.",
  },
  ZobaRecommendedJobMenuVisible: {
    title: "시냅스 추천작업 화면 열기",
    description: "시냅스 추천작업 메뉴를 표시하고 화면 진입을 허용합니다. 화면에는 불러오기·콜렉터에 띄우기·초기화 기능이 있습니다.",
    prerequisite: "메뉴를 표시하려면 운영·시냅스 메뉴 권한과 시냅스 추천작업 메뉴 표시 조건 권한이 모두 필요합니다.",
    scopeNote: "추천작업 화면 안의 변경 기능을 별도 권한으로 나누지 않습니다.",
  },
  AdminAccountMenuVisible: {
    title: "어드민 계정 관리 화면 열기",
    description: "계정의 어드민계정 리스트 메뉴와 화면을 엽니다. 같은 화면에 계정 권한 수정·일괄 수정·OTP 관리 기능이 있습니다.",
  },
  UserMenuVisible: {
    title: "유저 검색·상세 화면 열기",
    description: "유저 검색과 유저 상세 화면을 엽니다. 유저 개인정보와 결제내역 등을 확인할 수 있습니다.",
    prerequisite: "유저 화면은 본사 밴드 계정만 접근할 수 있습니다. 쿠폰·보증금·정보 수정 등은 각 기능의 추가 권한이 필요합니다.",
  },
  RideMenuVisible: {
    title: "라이드 목록 메뉴 열기",
    description: "사이드 메뉴에 라이드 목록으로 가는 메뉴를 표시합니다.",
    prerequisite: "환불·재결제·파일 다운로드는 각 기능의 추가 권한이 필요합니다.",
  },
  ScootMenuVisible: {
    title: "킥보드·자전거·모페드 메뉴 열기",
    description: "기기 메뉴와 킥보드/자전거 NEW·기존 버전·모페드 목록으로 가는 메뉴를 표시합니다.",
    prerequisite: "기기 상세 조작과 일괄 조작은 각 기능의 추가 권한과 계정 조건이 필요합니다.",
  },
  ControlScoot: {
    title: "킥보드·자전거 목록에서 일괄 조작 열기",
    description: "킥보드·자전거 목록에서 선택한 기기의 일괄 조작 버튼을 표시합니다.",
    prerequisite: "기기 메뉴 권한이 필요합니다. NEW 목록에서는 1~2,000대 선택 등 추가 조건이 있습니다.",
    scopeNote: "모페드 목록의 일괄 조작과 기기 상세 제어는 이 권한으로 제어하지 않습니다.",
  },
  ChangeUserButtonVisible: {
    title: "유저 이메일 수정·전화번호 삭제",
    description: "유저 상세의 유저 정보에서 이메일 수정과 번호 삭제 버튼을 활성화합니다.",
    prerequisite: "유저 메뉴 권한이 필요합니다.",
    scopeNote: "번호 삭제 기능입니다. 새 전화번호로 변경하는 기능은 포함하지 않습니다.",
  },
  IotRebootButtonVisible: {
    title: "기기 상세에서 IoT 재부팅",
    description: "기기 상세의 기기 제어 영역에 해당 기기 한 대를 재부팅하는 IoT 재부팅 버튼을 표시합니다.",
    prerequisite: "기기 상세에 접근할 수 있어야 합니다. S9·S11·W1·W7·W9 모델만 표시하며, 상태 변경이 금지된 밴드 계정은 제한합니다.",
    scopeNote: "일괄 조작에서는 이 버튼을 표시하지 않습니다.",
  },
  FranchiseUnitPriceEditEnable: {
    title: "지입 단가표 수정",
    description: "작업관리 상단의 지입 단가표에서 수정 버튼과 단가 입력칸을 활성화합니다.",
    prerequisite: "작업관리 메뉴 권한이 필요합니다.",
    scopeNote: "개인별 작업 수당 입력과 추가수당 기준단가 수정은 별도 권한입니다.",
  },
  DmTaskQuantityEditEnable: {
    title: "직원 작업 수량 입력",
    description: "작업관리에서 직원 행의 작업 수량을 입력할 수 있게 합니다.",
    prerequisite: "작업관리 메뉴·작업 수량 수정 진입 권한이 필요합니다. 입력할 항목에 따라 완료 수량 또는 배정 수량 입력 권한도 필요합니다.",
  },
  AddTaskDefinitionTypeEnable: {
    title: "새 작업 종류 생성",
    description: "기기 목록에서 새 작업 종류를 만드는 화면을 엽니다. 작업 타입과 표시 이름 등을 입력합니다.",
    prerequisite: "기기 메뉴 권한이 필요합니다.",
    scopeNote: "기기를 이미 만들어진 작업대상으로 지정하는 기능과는 다릅니다.",
  },
  CollectorDetailVisible: {
    title: "작업관리에서 콜렉터 상세 링크 표시",
    description: "작업 수량·수당 표의 콜렉터 이름을 상세 화면으로 이동하는 링크로 표시합니다.",
    prerequisite: "작업관리 메뉴 권한이 필요합니다.",
    scopeNote: "상세 링크만 제어합니다. 상세 화면의 프로필 수정·활성화는 별도 권한으로 나뉘어 있지 않습니다. 권한 설정·계정 삭제는 콜렉터 권한 설정·계정 삭제 권한으로 제어합니다.",
  },
  TowMenuVisible: {
    title: "견인 등록 화면 열기",
    description: "견인관리의 견인등록 메뉴를 표시하고 화면 진입을 허용합니다.",
  },
  CollectorAccountMenuVisible: {
    title: "콜렉터 계정 관리 화면 열기",
    description: "계정의 콜랙터계정 리스트 메뉴와 화면을 엽니다. 같은 화면에 계정 생성·프로필·권한 관리 기능이 있습니다.",
  },
};

// Historical consumers were verified in the master history on 2026-10-20.
// This describes the old connection, not a decision to retire the permission.
const historicalAdminPermissionPresentations: Record<
  string,
  { description: string; scopeNote?: string }
> = {
  NoticeMenuVisible: {
    description: "과거 스플래쉬 이미지 메뉴를 표시하던 권한입니다. 2022년 9월 해당 메뉴와 화면 경로가 제거됐습니다.",
  },
  RideWithLocationMenuVisible: {
    description: "과거 기기의 위치기록 메뉴를 표시하던 권한입니다. 2022년 11월 해당 메뉴가 제거됐습니다.",
  },
  BatteryMenuVisible: {
    description: "과거 배터리 메뉴를 표시하던 권한입니다. 2023년 1월 해당 메뉴가 주석 처리됐습니다.",
    scopeNote: "메뉴 표시와 직접 주소로 화면에 접근하는 조건은 별개입니다.",
  },
  ForceMenuVisible: {
    description: "과거 스윙포스 메뉴를 표시하던 권한입니다. 2022년 2월 메뉴가 주석 처리됐고 같은 해 11월 잔여 코드가 제거됐습니다.",
  },
  SettingMenuVisible: {
    description: "과거 신규앱 지오펜스 갱신 화면으로 가는 설정 메뉴를 표시하던 권한입니다. 2023년 1월 설정 메뉴와 갱신 화면이 제거됐습니다.",
  },
  ManagementSwingPass: {
    description: "과거 유저 상세의 스윙패스 편집 화면을 표시하던 권한입니다. 2024년 1월 해당 기능이 제거되고 CX 어드민 안내로 바뀌었습니다.",
    scopeNote: "현재 CX 멤버십 발급·상태 변경·환불/해지는 별도 SSO의 cx 도메인 admin 역할에서 관리합니다. 과거 스윙패스와 현재 멤버십 플랜의 대응은 별도 확인이 필요합니다.",
  },
  ChangeUserPhoneNumber: {
    description: "과거 유저 목록의 전화번호 변경 버튼과 변경 창을 표시하던 권한입니다. 2022년 9월 버튼과 변경 창이 주석 처리된 뒤 정리됐습니다.",
    scopeNote: "현재 유저 상세의 이메일 수정·번호 삭제 권한과는 별개입니다.",
  },
  CouponManageVisible: {
    description: "과거 쿠폰관리의 재사용 쿠폰 관리 메뉴를 표시하던 권한입니다. 2022년 9월 메뉴가 주석 처리됐고 같은 해 11월 잔여 코드가 제거됐습니다.",
    scopeNote: "현재 유저 상세의 쿠폰 조회·발급·삭제 권한과는 별개입니다.",
  },
  TaskTargetManualSetup: {
    description: "과거 조바 작업 상세의 작업대상·배치포인트 설정 버튼을 허용하던 권한입니다. 2023년 11월 시냅스 전환과 함께 해당 화면이 제거됐습니다.",
  },
  PartnerMenuVisible: {
    description: "과거 파트너 메뉴와 목록·상세 화면으로 연결되던 권한입니다. 2025년 4월 메뉴와 화면 경로가 제거됐습니다.",
  },
  RightToChangePrice: {
    description: "과거 운영 요금 변경 메뉴를 표시하던 권한입니다. 2022년 12월 메뉴가 주석 처리됐고 2023년 1월 잔여 코드가 제거됐습니다.",
  },
  ControlMultipleDropzone: {
    description: "과거 배치포인트 테이블의 일괄 변경 버튼을 표시하던 권한입니다. 2022년 11월 테이블 교체 과정에서 이 권한 검사 연결이 사라졌습니다.",
    scopeNote: "일괄 수정 기능은 새 화면에 남아 있지만 현재 이 키로 제어하지 않습니다.",
  },
};

export function getPermissionPresentation(
  permission: PermissionDTO,
  type: PermissionAccountType = "Admin"
): PermissionPresentation {
  if (type === "Collector") {
    return { title: permission.displayName, needsReview: false };
  }

  if (
    Object.prototype.hasOwnProperty.call(adminPermissionPresentations, permission.name)
  ) {
    return {
      ...adminPermissionPresentations[permission.name],
      needsReview: false,
    };
  }

  if (
    Object.prototype.hasOwnProperty.call(historicalAdminPermissionPresentations, permission.name)
  ) {
    return {
      title: permission.displayName,
      ...historicalAdminPermissionPresentations[permission.name],
      statusNotice: "과거 연결 확인 · 현재 사용처 미확인",
      needsReview: false,
    };
  }

  if (permission.name === "CampManagement") {
    return {
      title: permission.displayName,
      description: "현재 소스와 변경 이력에서 이 권한을 검사하는 코드를 찾지 못했습니다.",
      scopeNote: "현재 캠프관리 메뉴는 운영·시냅스 메뉴 권한으로 표시됩니다. 이 권한의 별도 사용 범위는 추가 확인이 필요합니다.",
      statusNotice: "사용처 확인 필요",
      needsReview: true,
    };
  }

  return {
    title: permission.displayName,
    description: "설명을 준비 중입니다. 필요한 화면과 작업을 확인해 주세요.",
    needsReview: true,
  };
}

export function matchesPermissionSearch(
  permission: PermissionDTO,
  query: string,
  type: PermissionAccountType = "Admin"
): boolean {
  const presentation = getPermissionPresentation(permission, type);
  const normalize = (text: string) => text.replace(/\s/g, "").toLocaleLowerCase();
  const searchText = [
    presentation.title,
    permission.displayName,
    permission.name,
    presentation.description,
    presentation.prerequisite,
    presentation.scopeNote,
    presentation.statusNotice,
  ].filter(Boolean).join(" ");

  return normalize(searchText).includes(normalize(query));
}
